import { useMemo } from 'react';
import { Button } from '@crazyegginc/hatch';

export function MetaAdsAccountSelectionDialog({
  accounts = [],
  errorMessage = null,
  pendingAccountId = null,
  onDismiss,
  onSelect,
}) {
  const uniqueAccounts = useMemo(() => {
    const dedupedAccounts = new Map();

    accounts.forEach((account) => {
      if (account?.id && !dedupedAccounts.has(account.id)) {
        dedupedAccounts.set(account.id, account);
      }
    });

    return Array.from(dedupedAccounts.values());
  }, [accounts]);

  if (uniqueAccounts.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[99998]">
      <button
        type="button"
        className="absolute inset-0 bg-mako-500/60"
        aria-label="Dismiss Meta Ads account picker"
        onClick={onDismiss}
      />
      <div
        role="dialog"
        aria-labelledby="meta-ads-account-selection-title"
        aria-modal="true"
        className="absolute left-1/2 top-1/2 z-[99999] flex max-h-[80vh] w-[560px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-auto rounded-[28px] bg-white p-7 shadow-2xl"
      >
        <div className="inline-flex w-fit items-center rounded-full border border-[#E5D0FF] bg-[#FBF7FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#5B3E8C]">
          Meta Ads
        </div>

        <h2
          id="meta-ads-account-selection-title"
          className="mt-4 text-[28px] font-semibold leading-tight text-black-pearl-500"
        >
          Choose the Meta ad account to connect
        </h2>

        <p className="mt-3 text-sm leading-6 text-lynch-500">
          This Meta login can access more than one ad account. Pick the account you want to attach to the currently
          selected site.
        </p>

        {errorMessage ? (
          <div
            className="mt-4 rounded-2xl border border-[#F8D7DA] bg-[#FFF5F5] px-4 py-3 text-sm font-medium text-[#8A2432]"
            data-testid="meta-ads-selection-error"
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          {uniqueAccounts.map((account) => {
            const { id: accountId, name } = account;
            const loading = pendingAccountId === accountId;

            return (
              <button
                key={accountId}
                type="button"
                className="flex items-center justify-between rounded-2xl border border-mystic-500 px-4 py-3 text-left transition hover:border-[#E5D0FF] hover:bg-[#FBF7FF]"
                onClick={() => onSelect(accountId)}
                disabled={Boolean(pendingAccountId)}
                data-testid={`meta-ads-account-${accountId}`}
              >
                <div>
                  {name ? <div className="text-sm font-semibold text-black-pearl-500">{name}</div> : null}
                  <div className={`text-xs font-medium text-lynch-500 ${name ? 'mt-1' : ''}`}>{accountId}</div>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5B3E8C]">
                  {loading ? 'Connecting...' : 'Connect'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Button variant="cancel" onClick={onDismiss}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
